import { Head, Link } from "@inertiajs/react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import * as LucideIcons from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import AppLayout from "@/layouts/app-layout";
import type { ActivityLogPageProps, BreadcrumbItem } from "@/types";

export default function ActivityLog({
  purchase_request,
  events,
}: ActivityLogPageProps) {
  const routes = useOrganizationRoutes();

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Solicitudes de compra", href: routes.purchaseRequests.index() },
    {
      title: `Solicitud #${purchase_request?.id ?? "N/A"}`,
      href: routes.purchaseRequests.show(purchase_request?.id ?? ""),
    },
    { title: "Historial de actividad", href: "#" },
  ];

  const getIconComponent = (iconName: string) => {
    // Convert kebab-case to PascalCase
    const pascalCase = iconName
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join("");

    const Icon = (
      LucideIcons as unknown as Record<
        string,
        React.ComponentType<{ className?: string }>
      >
    )[pascalCase];

    return Icon || LucideIcons.Circle;
  };

  const getColorClasses = (color: string) => {
    const colorMap: Record<string, { bg: string; ring: string; icon: string }> =
      {
        blue: {
          bg: "bg-blue-500",
          ring: "ring-white dark:ring-gray-900",
          icon: "text-white",
        },
        green: {
          bg: "bg-green-500",
          ring: "ring-white dark:ring-gray-900",
          icon: "text-white",
        },
        red: {
          bg: "bg-red-500",
          ring: "ring-white dark:ring-gray-900",
          icon: "text-white",
        },
        orange: {
          bg: "bg-orange-500",
          ring: "ring-white dark:ring-gray-900",
          icon: "text-white",
        },
        purple: {
          bg: "bg-purple-500",
          ring: "ring-white dark:ring-gray-900",
          icon: "text-white",
        },
        indigo: {
          bg: "bg-indigo-500",
          ring: "ring-white dark:ring-gray-900",
          icon: "text-white",
        },
        gray: {
          bg: "bg-gray-400 dark:bg-gray-600",
          ring: "ring-white dark:ring-gray-900",
          icon: "text-white",
        },
      };

    return colorMap[color] || colorMap.gray;
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return format(date, "d 'de' MMMM, yyyy 'a las' HH:mm", { locale: es });
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Historial - Solicitud ${purchase_request?.id ?? "N/A"}`} />

      <div className="container mx-auto px-4 py-6 max-w-5xl">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Historial de Actividad
            </h1>
            <p className="text-muted-foreground mt-1">
              Solicitud de compra #{purchase_request?.id ?? "N/A"}
              {purchase_request?.legacy_request_number && (
                <span className="ml-2 text-xs opacity-70">
                  (Nº Original: {purchase_request.legacy_request_number})
                </span>
              )}
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link
              href={routes.purchaseRequests.show(purchase_request?.id ?? "")}
            >
              Volver a la solicitud
            </Link>
          </Button>
        </div>

        {/* Timeline */}
        <Card>
          <CardHeader>
            <CardTitle>Línea de Tiempo</CardTitle>
          </CardHeader>
          <CardContent>
            {!events || events.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <p>No hay eventos registrados aún.</p>
              </div>
            ) : (
              <div className="flow-root">
                <ul role="list" className="-mb-8">
                  {events?.map((event, eventIdx) => {
                    const Icon = getIconComponent(event?.icon ?? "circle");
                    const colors = getColorClasses(event?.color ?? "gray");

                    return (
                      <li key={event?.id ?? `event-${eventIdx}`}>
                        <div className="relative pb-8">
                          {/* Connecting line */}
                          {eventIdx !== events?.length - 1 && (
                            <span
                              aria-hidden="true"
                              className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200 dark:bg-gray-700"
                            />
                          )}

                          <div className="relative flex space-x-3">
                            {/* Icon circle */}
                            <div>
                              <span
                                className={`flex size-8 items-center justify-center rounded-full ring-8 ${colors?.bg ?? "bg-gray-400"} ${colors?.ring ?? "ring-white"}`}
                              >
                                <Icon
                                  aria-hidden="true"
                                  className={`size-5 ${colors?.icon ?? "text-white"}`}
                                />
                              </span>
                            </div>

                            {/* Event content */}
                            <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                              <div className="flex-1">
                                <p className="text-sm text-gray-900 dark:text-gray-100">
                                  {event?.description ?? "Sin descripción"}
                                </p>
                                {event?.actor && (
                                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                    por{" "}
                                    <span className="font-medium">
                                      {event?.actor?.full_name ??
                                        event?.actor?.email ??
                                        "Usuario"}
                                    </span>
                                  </p>
                                )}
                              </div>
                              <div className="text-right text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                                <time dateTime={event?.timestamp ?? ""}>
                                  {event?.timestamp
                                    ? formatTimestamp(event.timestamp)
                                    : ""}
                                </time>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer with back button */}
        <div className="mt-6 flex justify-center">
          <Button variant="outline" asChild>
            <Link
              href={routes.purchaseRequests.show(purchase_request?.id ?? "")}
            >
              Volver a la solicitud
            </Link>
          </Button>
        </div>
      </div>
    </AppLayout>
  );
}
